import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { InvoiceEntity } from './invoice.entity';
import { MPaymentTransactionEntity } from './m-payment-transaction.entity';

/**
 * Ledger entry types. The ledger is APPEND-ONLY — corrections are
 * offsetting entries, never edits (mirrors Stripe customer balance
 * transactions / Shopify store credit transactions).
 */
export enum CustomerFundsLedgerType {
	/** Money deliberately placed on the customer's account (explicit
	 *  on-account portion of a Make Payment capture). Positive amount. */
	DEPOSIT = 'deposit',
	/** Account Funds payment captured (Make Payment or checkout).
	 *  Negative amount. */
	SPEND = 'spend',
	/** Spend returned to the pool: fulfillment invoiced for less than the
	 *  captured amount, or the Account Funds transaction was voided or
	 *  refunded. Positive amount. */
	SPEND_REVERT = 'spend_revert',
	/** Manual or system correction (e.g. reversing a deposit when its
	 *  source payment is voided). Either sign. */
	ADJUSTMENT = 'adjustment',
	/** Reserved for the upstream ERP credit-balance feed (HF Foods —
	 *  field TBD). Either sign. */
	ERP_SYNC = 'erp_sync',
}

/**
 * PD-14058 — pooled customer account funds, modeled as an explicit
 * append-only ledger. A client's available funds are SUM(amount) over
 * their non-deleted rows; nothing is ever inferred from unallocated
 * payment remainders (which made legacy history read as phantom money).
 */
@Index('fk_client__id', ['fkClientId'], {})
@Index('fk_m_payment_transaction__id', ['fkMPaymentTransactionId'], {})
@Index('fk_invoice__id', ['fkInvoiceId'], {})
@Entity('customer_funds_ledger', {}, { label: 'Customer Funds Ledger' })
export class CustomerFundsLedgerEntity extends BaseEntity {
	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column('varchar', { name: 'type', length: 32 })
	public type!: string;

	/** Signed: deposits/reverts positive, spends negative. */
	@Column('decimal', { name: 'amount', precision: 13, scale: 2 })
	public amount!: string;

	/** The payment transaction that deposited or spent the money. */
	@Column('int', {
		name: 'fk_m_payment_transaction__id',
		unsigned: true,
		nullable: true,
	})
	public fkMPaymentTransactionId!: number | null;

	/** The invoice a spend was applied to, when known. */
	@Column('int', { name: 'fk_invoice__id', unsigned: true, nullable: true })
	public fkInvoiceId!: number | null;

	/** Human-readable audit note ("Voided transaction 110", …). */
	@Column('varchar', { name: 'note', nullable: true, length: 255 })
	public note!: string | null;

	@ManyToOne(() => ClientEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	@ManyToOne(() => MPaymentTransactionEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([
		{ name: 'fk_m_payment_transaction__id', referencedColumnName: 'id' },
	])
	public fkMPaymentTransaction!: MPaymentTransactionEntity | null;

	@ManyToOne(() => InvoiceEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_invoice__id', referencedColumnName: 'id' }])
	public fkInvoice!: InvoiceEntity | null;
}
